import React, { useEffect, useState } from 'react';
import { Card, List, Badge, Tag, Typography, Spin, Empty, Button, Space } from 'antd';
import {
  BellOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { partnerAPI } from '../services/api';
import { fmtDateTime } from '../utils/date';

const { Title, Text } = Typography;

interface Notification {
  id: number;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  created_at: string;
  schedule_id?: number;
}

const NotificationPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const res = await partnerAPI.notifications();
      setNotifications(res.data);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'status_change': return <ClockCircleOutlined style={{ color: '#f59e0b' }} />;
      case 'survey_complete': return <CheckCircleOutlined style={{ color: '#10b981' }} />;
      case 'report_ready': return <FileTextOutlined style={{ color: '#f59e0b' }} />;
      case 'test_started': return <ExperimentOutlined style={{ color: '#f97316' }} />;
      case 'test_completed': return <CheckCircleOutlined style={{ color: '#10b981' }} />;
      default: return <BellOutlined style={{ color: '#f59e0b' }} />;
    }
  };

  const getTypeTag = (type: string) => {
    switch (type) {
      case 'status_change': return <Tag color="orange">상태 변경</Tag>;
      case 'survey_complete': return <Tag color="green">조사 완료</Tag>;
      case 'report_ready': return <Tag color="blue">보고서 준비</Tag>;
      case 'test_started': return <Tag color="gold">시험 개시</Tag>;
      case 'test_completed': return <Tag color="green">시험 완료</Tag>;
      default: return <Tag>알림</Tag>;
    }
  };

  const formatDate = (dateStr: string) => fmtDateTime(dateStr);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#92400e' }}>
            <BellOutlined style={{ marginRight: 8 }} />
            알림
            {unreadCount > 0 && (
              <Badge
                count={unreadCount}
                style={{ marginLeft: 8, backgroundColor: '#f59e0b' }}
              />
            )}
          </Title>
          <Text type="secondary">시험 상태 변경 알림을 확인하세요</Text>
        </div>
      </div>

      <Card size="small">
        {notifications.length === 0 ? (
          <Empty description="알림이 없습니다" />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={notifications}
            renderItem={(item) => (
              <List.Item
                style={{
                  background: item.is_read ? 'transparent' : '#fffbeb',
                  padding: '16px',
                  borderRadius: 8,
                  marginBottom: 4,
                }}
                actions={[
                  getTypeTag(item.type),
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: item.is_read ? '#f8fafc' : '#fef3c7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                    }}>
                      {getTypeIcon(item.type)}
                    </div>
                  }
                  title={
                    <Space>
                      {!item.is_read && (
                        <Badge dot color="#f59e0b" />
                      )}
                      <Text strong={!item.is_read}>{item.title}</Text>
                    </Space>
                  }
                  description={
                    <div>
                      <div style={{ color: '#475569', fontSize: 13, marginBottom: 4 }}>
                        {item.message}
                      </div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {formatDate(item.created_at)}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
};

export default NotificationPage;
